import { PERCEPTION_MODELS } from 'constants/routePaths';

import * as React from 'react';

import { ArrowBack, Visibility } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';

import AnchorEditor from './AnchorEditor';
import AnchorList from './AnchorList';
import AudienceFrame from './AudienceFrame';
import { demoGrid } from './demographic';
import { normSetMembers } from './normSetUtils';
import PerceptionModelError from './PerceptionModelError';
import TriggerReportButton from './TriggerReportButton';
import useTranslationRow from './useTranslationRow';
import { PageHeader, PagePaper, PageTitle } from '../../styles/themeComponents';

/**
 * A saved translation.
 *
 * Three things are shown, and only one of them can be moved from here.
 *
 * The **audience** is the population the numbers describe, and it is frozen:
 * every number this translation produced was rendered against that grid, so a
 * different audience is a different translation.
 *
 * The **norm set** is the products, which is what the study is about. It was
 * settled when the norm set was built and is frozen with it — a different set
 * of products is a new norm set, not an edit of this one. So it is listed here
 * and nothing more.
 *
 * The **anchors** are the reference those products were measured from, and they
 * are the exception: moving them re-measures every distance in the same write,
 * so the numbers always describe a comparison that was actually made. They live
 * on the parent norm set, which is why the editor writes there — the membership
 * it holds is untouched, and the siblings reading against it are named before
 * the save.
 */

type Props = { normSetId: number; translationId: number };

/**
 * REQUESTED is the resting state, not progress: the row exists and nothing has
 * been asked of it, which is exactly what makes it eligible. STARTED means the
 * button has been pushed and a job is running.
 */
const STATUS_COLOUR: Record<string, 'default' | 'info' | 'success' | 'error'> =
  {
    REQUESTED: 'default',
    STARTED: 'info',
    SUCCESS: 'success',
    FAILED: 'error',
  };

const TranslationDetail: React.FC<Props> = ({ normSetId, translationId }) => {
  const history = useHistory();
  const {
    normSet,
    translation,
    modelled,
    producerId,
    translationCount,
    loading,
    error,
  } = useTranslationRow(normSetId, translationId);

  const [editingAnchors, setEditingAnchors] = React.useState(false);

  /**
   * Everything derived, memoised above the guards below, because a hook cannot
   * be called after an early return.
   */
  const products = React.useMemo(
    () => Array.from(new Set(translation?.products ?? [])).sort(),
    [translation],
  );
  const anchors = React.useMemo(() => normSet?.anchors ?? [], [normSet]);
  const members = React.useMemo(
    () => normSetMembers(normSet?.normSet ?? null).map((m) => m.product),
    [normSet],
  );
  const unmodelled = React.useMemo(
    () => (modelled.size ? products.filter((p) => !modelled.has(p)) : []),
    [products, modelled],
  );

  const backToNormSet = () => history.push(`${PERCEPTION_MODELS}/${normSetId}`);

  if (loading) return <PagePaper>Loading translation…</PagePaper>;

  if (error) {
    return (
      <PagePaper>
        <PerceptionModelError error={error} />
      </PagePaper>
    );
  }

  if (!normSet || !translation) {
    return (
      <PagePaper>
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={backToNormSet}>
              Back
            </Button>
          }
        >
          No translation #{translationId} on this norm set.
        </Alert>
      </PagePaper>
    );
  }

  const cells = demoGrid(translation.demographic as never).length;

  return (
    <PagePaper>
      {/* One level up is the norm set this translation belongs to. Above the
          header, like every other page in the flow — it used to sit at the
          bottom, so the deepest page hid its exit below the fold. */}
      <Button
        size="small"
        startIcon={<ArrowBack fontSize="small" />}
        onClick={backToNormSet}
        sx={{ textTransform: 'none', mb: 1 }}
      >
        {normSet.name}
      </Button>

      <PageHeader>Translation · on {normSet.name}</PageHeader>
      <PageTitle>{translation.name}</PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          size="small"
          label={translation.status}
          color={STATUS_COLOUR[translation.status] ?? 'default'}
          variant={translation.status === 'REQUESTED' ? 'outlined' : 'filled'}
        />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Translation #{translation.id} · job {normSet.perceptionModelJobId} ·
          created {new Date(translation.createdAt).toLocaleDateString()}
        </Typography>
      </Box>

      {translation.status === 'FAILED' && (
        <Alert severity="error" sx={{ mt: 1 }}>
          The last render failed
          {translation.statusReason ? `: ${translation.statusReason}` : '.'}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mt: 2 }}>
        <Alert severity="info">
          The audience and the norm set are fixed — a different audience is a
          different translation, and a different set of products is a different
          norm set. The anchors can move: doing so re-measures every distance in
          the same write, so the numbers always describe a comparison that was
          actually made.
        </Alert>

        <AudienceFrame demographic={translation.demographic} />

        <Divider />

        <AnchorList
          anchors={anchors}
          caption={`What the ${products.length} ${
            products.length === 1 ? 'product' : 'products'
          } in this norm set were measured from, carried from “${normSet.name}”.`}
          onChangeAnchors={() => setEditingAnchors(true)}
        />

        {/* Writes to the parent, because that is where the anchors and the
            distances taken from them live. Mounted here so the analyst can
            re-aim the comparison without leaving the translation they are
            reading — the trip to the norm set page and back was the whole
            friction. */}
        <AnchorEditor
          normSetId={normSet.id}
          jobId={normSet.perceptionModelJobId}
          producerId={producerId}
          anchors={anchors}
          members={members}
          translationCount={translationCount}
          open={editingAnchors}
          onClose={() => setEditingAnchors(false)}
        />

        <Divider />

        <Box>
          <Typography variant="subtitle1">Norm set</Typography>
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
          >
            {products.length} {products.length === 1 ? 'product' : 'products'} ×{' '}
            {cells} {cells === 1 ? 'cell' : 'cells'} = {products.length * cells}{' '}
            renders. Frozen with “{normSet.name}” when it was built — a
            different set of products is a new norm set.
          </Typography>

          {/* A link rather than a list. The membership cannot be changed from
              anywhere, so putting a hundred rows on the page it cannot be
              changed from only buries the two controls that do something. The
              norm set's own page is where it is read. */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<Visibility fontSize="small" />}
            onClick={backToNormSet}
            sx={{ textTransform: 'none' }}
          >
            View all {products.length}{' '}
            {products.length === 1 ? 'product' : 'products'}
          </Button>

          {/* Not a fact about the norm set but about this render: an unmodelled
              product is not skipped, it stops the whole job. So it is named
              here rather than left to the page one click away. */}
          {unmodelled.length > 0 && (
            <Typography
              variant="caption"
              sx={{ color: 'warning.main', display: 'block', mt: 1 }}
            >
              {unmodelled.length} not in this model — the render would stop on{' '}
              {unmodelled.slice(0, 3).join(', ')}
              {unmodelled.length > 3 && '…'}
            </Typography>
          )}
        </Box>

        {products.length === 0 && (
          <Alert severity="warning">
            This translation carries no products, so there is nothing to render.
            It came from a norm set with no members.
          </Alert>
        )}

        <Divider />

        <Box>
          <TriggerReportButton
            translationId={translation.id}
            products={products.length}
            cells={cells}
            status={translation.status}
            startedAt={translation.startedAt}
          />
        </Box>
      </Stack>
    </PagePaper>
  );
};

export default TranslationDetail;
